<script setup>
// Asks why before a publication or a restoration: the reason goes to the version and to the audit log, so it is
// required. `open()` clears the field; confirming without a reason shows the error and emits nothing.
import { computed, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  Field,
  FieldError,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';

defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  confirmLabel: { type: String, required: true },
  isLoading: Boolean,
});
const emit = defineEmits(['confirm']);

const { t } = useI18n();
const id = useId();
const dialog = ref(null);
const reason = ref('');
const tried = ref(false);
const missing = computed(() => tried.value && !reason.value.trim());

const open = () => {
  reason.value = '';
  tried.value = false;
  dialog.value?.open();
};
const close = () => dialog.value?.close();

const confirm = () => {
  tried.value = true;
  if (!reason.value.trim()) return;
  emit('confirm', reason.value.trim());
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialog"
    width="lg"
    :title="title"
    :description="description"
    :confirm-button-label="confirmLabel"
    :is-loading="isLoading"
    @confirm="confirm"
  >
    <Field :data-invalid="missing || undefined">
      <FieldLabel :for="id">
        {{ t('POLARIS.ASSISTANTS.VOICE.REASON.LABEL') }}
      </FieldLabel>
      <Textarea
        :id="id"
        v-model="reason"
        :placeholder="t('POLARIS.ASSISTANTS.VOICE.REASON.PLACEHOLDER')"
        :aria-invalid="missing || undefined"
        :aria-describedby="missing ? `${id}-error` : undefined"
      />
      <FieldError v-if="missing" :id="`${id}-error`">
        {{ t('POLARIS.ASSISTANTS.VOICE.REASON.ERROR') }}
      </FieldError>
    </Field>
  </Dialog>
</template>
